"use strict";
/* globals module */
/**
 * @module mediaVaultClient
 * @description  Media Vault Client provides Baasic route templates which can be expanded to Baasic REST URIs. Various services can use Baasic Media Vault Route Service to obtain needed routes while other routes will be obtained through HAL. By convention, all route services use the same function names as their corresponding services.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var httpApi_1 = require("../../httpApi");
var _1 = require("./");
var MediaVaultClient = /** @class */ (function () {
    function MediaVaultClient(mediaVaultRoute, apiClient, mediaVaultStreamsClient, mediaVaultBatchClient, mediaVaultSettingsClient, mediaVaultProcessingProviderSettingsClient) {
        this.mediaVaultRoute = mediaVaultRoute;
        this.apiClient = apiClient;
        this.mediaVaultStreamsClient = mediaVaultStreamsClient;
        this.mediaVaultBatchClient = mediaVaultBatchClient;
        this.mediaVaultSettingsClient = mediaVaultSettingsClient;
        this.mediaVaultProcessingProviderSettingsClient = mediaVaultProcessingProviderSettingsClient;
    }
    Object.defineProperty(MediaVaultClient.prototype, "routeDefinition", {
        get: function () {
            return this.mediaVaultRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MediaVaultClient.prototype, "streams", {
        get: function () {
            return this.mediaVaultStreamsClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MediaVaultClient.prototype, "batch", {
        get: function () {
            return this.mediaVaultBatchClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MediaVaultClient.prototype, "settings", {
        get: function () {
            return this.mediaVaultSettingsClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MediaVaultClient.prototype, "processingProviderSettings", {
        get: function () {
            return this.mediaVaultProcessingProviderSettingsClient;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of media vault resources matching the given criteria.
     * @method
     * @param options Query resource options object.
     * @returns A promise that is resolved once the find action has been performed.
     * @example mediaVaultClient.find({
                  pageNumber : 1,
                  pageSize : 10,
                  orderBy : '<field>',
                  orderDirection : '<asc|desc>',
                  search : '<search-phrase>'
               })
               .then(function (collection) {
                   // perform success action here
              },
               function (response, status, headers, config) {
                   // perform error handling here
              });
   **/
    MediaVaultClient.prototype.find = function (options) {
        return this.apiClient.get(this.routeDefinition.find(options));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns requested media vault resource.
     * @method
     * @param id Media vault id which uniquely identifies media vault resource that needs to be retrieved.
     * @param options Query resource options object.
     * @returns A promise that is resolved once the get action has been performed.
     * @example mediaVaultClient.get('<media-vault-id>')
                    .then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                         // perform error handling here
                    });
     **/
    MediaVaultClient.prototype.get = function (id, options) {
        return this.apiClient.get(this.mediaVaultRoute.get(id, options));
    };
    /**
     * Returns a promise that is resolved once the update media vault action has been performed; this action will update a media vault resource if successfully completed. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `mediaVaultRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(mediaVaultEntry);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data A media vault object used to update specified media vault resource.
     * @returns A promise that is resolved once the update media vault action has been performed.
     * @example // mediaVaultEntry is a media vault resource previously fetched using get action.
                    mediaVaultEntry.description = '<description>';
                    mediaVaultClient.update(mediaVaultEntry)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    MediaVaultClient.prototype.update = function (data) {
        return this.apiClient.put(this.routeDefinition.update(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the remove action has been performed. This action will remove one or many media vault resources from the system if successfully completed. If derived resource's format is passed, such as `width` and `height` for the image type of media vault resource, the operation will remove just derived resource. Otherwise, specified media vault and all its accompanying derived resources will be removed from the system. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply baasicMediaVaultRouteService route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(mediaVaultEntry);
     * let uri = params['model'].links('delete').href;
     * ```
     * @method
     * @param data Media vault object used to delete specific Media vault resource from the system.
     * @param options Options object.
     * @example // mediaVaultEntry is a media vault resource previously fetched using get action. The following action will remove the original media vault resource and all accompanying derived media vault resources.
                   mediaVaultClient.remove(mediaVaultEntry)
                       .then(function (data) {
                           // perform success action here
                       },
                        function (response, status, headers, config) {
                            // perform error handling here
                       });
               // mediaVaultEntry is a media vault resource previously fetched using get action. The following action will remove derived media vault resource only.
                   mediaVaultClient.remove(mediaVaultEntry, {width: <width>, height: <height>})
                       .then(function (data) {
                           // perform success action here
                       },
                        function (response, status, headers, config) {
                            // perform error handling here
                       });
    **/
    MediaVaultClient.prototype.remove = function (data, options) {
        return this.apiClient.delete(this.mediaVaultRoute.delete(data, options));
    };
    var _a, _b, _c, _d, _e, _f;
    MediaVaultClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(_1.TYPES.MediaVaultRoute)),
        tslib_1.__param(1, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__param(2, inversify_1.inject(_1.TYPES.MediaVaultStreamsClient)),
        tslib_1.__param(3, inversify_1.inject(_1.TYPES.MediaVaultBatchClient)),
        tslib_1.__param(4, inversify_1.inject(_1.TYPES.MediaVaultSettingsClient)),
        tslib_1.__param(5, inversify_1.inject(_1.TYPES.MediaVaultProcessingProviderSettingsClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof _1.MediaVaultRoute !== "undefined" && _1.MediaVaultRoute) === "function" ? _a : Object, typeof (_b = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _b : Object, typeof (_c = typeof _1.MediaVaultStreamsClient !== "undefined" && _1.MediaVaultStreamsClient) === "function" ? _c : Object, typeof (_d = typeof _1.MediaVaultBatchClient !== "undefined" && _1.MediaVaultBatchClient) === "function" ? _d : Object, typeof (_e = typeof _1.MediaVaultSettingsClient !== "undefined" && _1.MediaVaultSettingsClient) === "function" ? _e : Object, typeof (_f = typeof _1.MediaVaultProcessingProviderSettingsClient !== "undefined" && _1.MediaVaultProcessingProviderSettingsClient) === "function" ? _f : Object])
    ], MediaVaultClient);
    return MediaVaultClient;
}());
exports.MediaVaultClient = MediaVaultClient;
/**
 * @overview
 ***Notes:**
 - Refer to the [REST API documentation](http://dev.baasic.com/api/reference/home) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route service.
 */ 
